


























































































































































import { Mixins, Component, Watch } from 'vue-property-decorator'
import { Header } from '@/components/molecules/SmTableData.vue'

import { CLOSURE_PERIOD_SETTINGS, OWNER_NOTIFICATION_TYPE } from '@/constants/schema-constants'
import { PAGE_TYPES } from '@/constants/ux-constants'
import type { PageType } from '@/constants/ux-constants'

import { Building, BuildingsGetRequest } from '@/dtos/buildings/get'
import { ClosurePeriodSetting, ClosurePeriodSettingsGetRequest } from '@/dtos/closure-period-settings/get'
import { ClosurePeriodSettingDeleteRequest } from '@/dtos/closure-period-settings/delete'
import { ClosurePeriodSettingPostRequest } from '@/dtos/closure-period-settings/post'
import { ClosurePeriodSettingPutRequest } from '@/dtos/closure-period-settings/put'

import { BusinessDate } from '@/libs/business-date'
import { CurrentAdminManager } from '@/mixins/current-admin-manager'
import { FormInputs } from '@/pages/closure-period-settings/ClosurePeriodSettingCreateEditModal.vue'

import { buildingsModule } from '@/stores/buildings-store'
import { closurePeriodSettingsModule } from '@/stores/closure-period-settings-store'
import { OwnerNotificationPinningBuildingsGetRequest } from '@/dtos/buildings/owner-notifications/pinning/get'

@Component({
  components: {
    SmBtn: () => import('@/components/atoms/SmBtn.vue'),
    SmText: () => import('@/components/atoms/SmText.vue'),

    SmTableDataExternalPaging: () => import('@/components/molecules/SmTableDataExternalPaging.vue'),

    SmDialogText: () => import('@/components/organisms/dialog/SmDialogText.vue'),

    SmTemplate: () => import('@/components/templates/SmTemplate.vue'),

    ClosurePeriodSettingCreateEditModal: () => import('@/pages/closure-period-settings/ClosurePeriodSettingCreateEditModal.vue'),
  }
})
export default class ClosurePeriodSettingsPage extends Mixins(CurrentAdminManager) {
  CLOSURE_PERIOD_SETTINGS = Object.freeze(CLOSURE_PERIOD_SETTINGS)
  PAGE_TYPES = Object.freeze(PAGE_TYPES)

  tableHeaders:Header[] = [
    new Header({ text: '休業期間', value: 'closureStartAt' }),
    new Header({ text: 'メモ', value: 'memo', filterable: false }),
    new Header({ text: '', value: 'updateBtn', filterable: false }),
    new Header({ text: '', value: 'deleteBtn', filterable: false }),
  ]

  @Watch('pageNum', { immediate: false })
  scrollToTop(): void {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  private buildings: Building[] = []
  private get closurePeriodSettings(): ClosurePeriodSetting[] { return closurePeriodSettingsModule.closurePeriodSettingsGet.closurePeriodSettings }
  private get settingsCount(): number { return closurePeriodSettingsModule.closurePeriodSettingsCountGet ?? 0 }

  pageNum = 1
  isPinningUpdateBuildingCount = 0
  pinningUpdateBuildingNameList = ''

  private ownerNotificationId?: string | null = null
  private closurePeriodSettingId?: string | null = null
  private closureStartDate!: string
  private closureEndDate!: string
  private memo?: string
  private pageType!: PageType

  async created():Promise<void> {
    this.pageNum = 1 // ここで初期ページを設定
    await this.fetchClosurePeriodSettings()
    await this.fetchBuildings()
  }

  private businessDate = new BusinessDate()

  // 翌日の0:00までの日時と比較したいため、一日増やした日時を返す
  addDays(date: Date, days: number) : Date {
    return this.businessDate.getAfterDay(date, days)
  }

  toHyphenatedDateString(date: Date) :string {
    return this.businessDate.toHyphenatedDateString(date)
  }

  async fetchClosurePeriodSettings(): Promise<void> {
    const subsequentTake = 20
    const take = subsequentTake
    const offset = take * (this.pageNum - 1)
    const request = new ClosurePeriodSettingsGetRequest(offset, take)
    await closurePeriodSettingsModule.fetchClosurePeriodSettings(request)
  }

  async fetchBuildings(): Promise<void> {
    const request = new BuildingsGetRequest(0, 999, undefined, undefined, true)
    await buildingsModule.fetchBuildings(request)
    this.buildings = buildingsModule.buildingsGet.buildings
  }

  // ページング機能、ボタン押下時の処理
  async onClickPageBtn(pageNum: number): Promise<void> {
    this.pageNum = pageNum
    await this.fetchClosurePeriodSettings()
  }

  // --------------- 休業期間新規設定・編集モーダル関連の処理 ---------------
  handleUpdate({ closureStartDate, closureEndDate, memo }: FormInputs): void {
    this.closureStartDate = closureStartDate
    this.closureEndDate = closureEndDate
    this.memo = memo
  }

  isModalVisible = false
  openClosurePeriodSettingCreateEditModal(type: PageType, closurePeriodSetting?: FormInputs): void {
    if (type === PAGE_TYPES.EDIT && closurePeriodSetting) {
      this.closurePeriodSettingId = closurePeriodSetting.closurePeriodSettingId
      this.ownerNotificationId = closurePeriodSetting.ownerNotificationId
      this.closureStartDate = closurePeriodSetting.closureStartDate
      this.closureEndDate = closurePeriodSetting.closureEndDate
      this.memo = closurePeriodSetting.memo
    }
    this.pageType = type
    this.isModalVisible = true
  }

  isPinningUpdateIndividualAlertVisible = false
  // 固定されたお知らせの重複物件の確認
  async fetchNotificationPinningBuildings(closurePeriodSetting: FormInputs): Promise<void> {
    const req = new OwnerNotificationPinningBuildingsGetRequest()
    req.ownerNotificationTypes = [OWNER_NOTIFICATION_TYPE.NOTIFIED_BY_ADMIN, OWNER_NOTIFICATION_TYPE.NOTIFIED_BY_ADMIN_CLOSURE_PERIOD]

    const twoWeekLater = this.addDays(new Date(), 14)
    const reqClosureStartDate = new Date(closurePeriodSetting.closureStartDate)
    if (reqClosureStartDate > twoWeekLater) {
      reqClosureStartDate.setHours(9, 0, 0, 0) // 休業期間のお知らせ予約投稿時刻は一律9:00で設定されるため
      req.scheduledPostDateTime = this.businessDate.toHyphenatedDateTimeString(this.addDays(reqClosureStartDate, -14))
    }
    if (this.pageType === PAGE_TYPES.EDIT && closurePeriodSetting.ownerNotificationId) {
      req.ownerNotificationId = closurePeriodSetting.ownerNotificationId
    }

    await buildingsModule.fetchNotificationPinningBuildings(req)

    const pinningUpdateTargets = this.buildings.filter(b =>
      buildingsModule.ownerNotificationPinningBuildingsGet.buildingIds.includes(b.buildingId)
    )

    this.isPinningUpdateBuildingCount = pinningUpdateTargets.length
    this.pinningUpdateBuildingNameList = `・${pinningUpdateTargets.map(b => b.buildingName).join('\n・')}`

    if (this.isPinningUpdateBuildingCount > 0) {
      this.isPinningUpdateIndividualAlertVisible = true
      return
    }
    this.executeClosurePeriodSetting(closurePeriodSetting)
  }

  async executeClosurePeriodSetting(closurePeriodSetting: FormInputs): Promise<void> {
    this.isPinningUpdateIndividualAlertVisible = false
    // 休業期間 更新
    if (this.pageType === PAGE_TYPES.EDIT) {
      if (closurePeriodSetting.closurePeriodSettingId && closurePeriodSetting.ownerNotificationId) {
        const req = new ClosurePeriodSettingPutRequest(
          closurePeriodSetting.closurePeriodSettingId,
          closurePeriodSetting.ownerNotificationId,
          closurePeriodSetting.closureStartDate,
          closurePeriodSetting.closureEndDate,
          closurePeriodSetting.memo
        )
        await closurePeriodSettingsModule.putClosurePeriodSetting(req)
      }
    } else {
      // 休業期間 新規設定
      const req = new ClosurePeriodSettingPostRequest(
        closurePeriodSetting.closureStartDate,
        closurePeriodSetting.closureEndDate,
        closurePeriodSetting.memo
      )
      await closurePeriodSettingsModule.postClosurePeriodSetting(req)
    }
    this.isModalVisible = false
    this.pageNum = 1
    await this.fetchClosurePeriodSettings()
    await this.fetchBuildings()
  }

  async closeIsPinningUpdateIndividualAlertDialog(): Promise<void> {
    this.isPinningUpdateIndividualAlertVisible = false
    this.isModalVisible = false
    await this.fetchClosurePeriodSettings()
    await this.fetchBuildings()
  }

  // 休業期間 削除
  isDeleteClosurePeriodSettingDialogVisible = false
  private selectedClosurePeriodSettingId: string | null = null;
  private get isDeleteModalVisible(): boolean { return this.isDeleteClosurePeriodSettingDialogVisible }
  private set isDeleteModalVisible(newValue: boolean) {
    this.isDeleteClosurePeriodSettingDialogVisible = !this.isDeleteClosurePeriodSettingDialogVisible
  }

  prepareDeletion(closurePeriodSettingId: string): void {
    this.selectedClosurePeriodSettingId = closurePeriodSettingId
    this.isDeleteClosurePeriodSettingDialogVisible = true
  }

  async executeDeleteClosurePeriodSetting(): Promise<void> {
    if (this.selectedClosurePeriodSettingId) {
      const deleteClosurePeriodSettingReq = new ClosurePeriodSettingDeleteRequest(this.selectedClosurePeriodSettingId)
      await closurePeriodSettingsModule.deleteClosurePeriodSetting(deleteClosurePeriodSettingReq)
      this.isDeleteModalVisible = false
      this.selectedClosurePeriodSettingId = null
      this.pageNum = 1

      await this.fetchClosurePeriodSettings()
      await this.fetchBuildings()
    }
  }
}
